import { takeEvery, put } from 'redux-saga/effects';
import ActionTypes from '../../../../Data/ActionTypes';
import {
  hasPhoneConstraint,
  hasPostcodeConstraint,
} from '../../../../Components/__helpers__/component.helpers';
import {
  clearSelectedOptionsAndValue,
  selectOptionAndUpdateValue,
  setAutoCompleteInputValue,
  setComponentValidationStatus,
  setSelectedOptions,
  updateDefaultValue,
  updateMaskedValue,
  updateValue,
} from '../../slices/components';
import { validatePhoneSaga } from '../ValidatePhone/validatePhone';
import { validatePostcodeSaga } from '../ValidatePostcode/validatePostcode';
import { handleAnswerQuestionSideEffectsSaga } from '../../../../Hooks/actionCreators';
import { mask } from '../../../../Utils/masking';
import { ComponentTypes } from '../../../../Data/Components';

/**
 * populateFormFieldWorker
 *
 * Used to pre populate form fields with given values ahead of the customer seeing the input field.
 *
 * @param {Object} action - The action object containing the payload.
 * @param {Object} action.payload - The payload object containing the following properties:
 *   - {Object} component: The component object containing the component details.
 *   - {string} countryCode: The country code.
 *   - {string} value: The value to populate the field with.
 *   - {string} iFrameId: The iframe ID.
 */
export function* populateFormFieldWorker(action) {
  const { countryCode, value, iFrameId, formId, component } = action.payload;

  const componentId = component?.component?.id;
  const formFieldType = component?.component?.type;
  const constraints = component?.validation?.constraints;
  const componentOptions = component?.input?.options;

  const selectedOption =
    typeof componentOptions === 'object'
      ? componentOptions.find(
          (option) =>
            value &&
            option.key &&
            option.value &&
            (option.key.toLowerCase() === value.toString().toLowerCase() ||
              option.value.toLowerCase() === value.toString().toLowerCase())
        )
      : null;

  const optionIdString = String(selectedOption?.id);
  const optionValue = selectedOption?.value || '';

  const includesPhoneConstraints = hasPhoneConstraint({
    component,
  });

  const includesPostcodeConstraints = hasPostcodeConstraint({
    component,
  });

  switch (formFieldType) {
    case ComponentTypes.HIDDEN:
      yield put(updateDefaultValue({ componentId, value }));
      break;
    case ComponentTypes.TEXTFIELD:
      if (includesPhoneConstraints) {
        yield put(updateValue({ componentId, value }));
        yield put(
          validatePhoneSaga({
            country: countryCode,
            number: value,
            componentId,
            iFrameId,
            constraints,
          })
        );
      } else if (includesPostcodeConstraints) {
        yield put(updateValue({ componentId, value }));
        yield put(
          validatePostcodeSaga({
            countryCode,
            value,
            componentId,
            iFrameId,
          })
        );
        const maskPattern = component?.input?.maskPattern;
        if (maskPattern) {
          yield put(
            updateMaskedValue({
              componentId,
              maskedValue: mask({ maskPattern, value }),
            })
          );
        }
      } else {
        yield put(updateValue({ componentId, value }));
        yield put(
          setComponentValidationStatus({
            componentId,
            countryCode,
            iFrameId,
            formId,
          })
        );
      }
      yield put(handleAnswerQuestionSideEffectsSaga({ componentId }));
      break;
    case ComponentTypes.SELECT:
      yield put(
        updateValue({
          componentId,
          value: optionValue,
        })
      );
      yield put(
        setSelectedOptions({
          componentId,
          selectedOptions: [optionIdString],
        })
      );
      yield put(
        setComponentValidationStatus({
          componentId,
          countryCode,
          iFrameId,
          formId,
        })
      );
      yield put(handleAnswerQuestionSideEffectsSaga({ componentId }));
      break;
    case ComponentTypes.RADIOS:
      yield put(
        clearSelectedOptionsAndValue({
          componentId,
        })
      );
      yield put(
        selectOptionAndUpdateValue({
          componentId,
          optionId: optionIdString,
        })
      );
      yield put(
        setComponentValidationStatus({
          componentId,
          countryCode,
          iFrameId,
          formId,
        })
      );
      yield put(handleAnswerQuestionSideEffectsSaga({ componentId }));
      break;
    case ComponentTypes.CHECKBOXES:
      yield put(
        selectOptionAndUpdateValue({
          componentId,
          optionId: optionIdString,
        })
      );
      yield put(
        setComponentValidationStatus({
          componentId,
          countryCode,
          iFrameId,
          formId,
        })
      );
      break;
    case ComponentTypes.GOOGLE_ADDRESS:
      yield put(setAutoCompleteInputValue({ componentId, value }));
      yield put(updateValue({ componentId, value }));
      yield put(
        setComponentValidationStatus({
          componentId,
          iFrameId,
          formId,
        })
      );
      yield put(handleAnswerQuestionSideEffectsSaga({ componentId }));
      break;
    default:
      // eslint-disable-next-line no-console
      console.warn(`Unexpected form field type: ${formFieldType}`);
      break;
  }
}

/**
 * Redux Watcher
 */
export default function* populateFormFieldWatcher() {
  yield takeEvery(ActionTypes.POPULATE_FORM_FIELD, populateFormFieldWorker);
}
